
import React, { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, Image, MessageSquare } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAIIntegration } from '@/hooks/useAIIntegration';
import AnimatedImageUploader from './AnimatedImageUploader';

interface AIContentGeneratorProps {
  bannerSize: number;
  onContentGenerated: (text: string) => void;
  onImageGenerated: (imageUrl: string | null) => void;
  setIsAnimatedGif?: (isAnimated: boolean) => void;
}

const AIContentGenerator: React.FC<AIContentGeneratorProps> = ({ 
  bannerSize, 
  onContentGenerated, 
  onImageGenerated,
  setIsAnimatedGif
}) => {
  const [prompt, setPrompt] = useState('');
  const [generatingText, setGeneratingText] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiResponse, setAIResponse] = useState<string>('');
  const [creativity, setCreativity] = useState(50);
  const [textType, setTextType] = useState('slogan');
  const { toast } = useToast();
  const { serviceStatus } = useAIIntegration();
  
  const handleTextGeneration = async () => {
    if (!prompt) {
      toast({
        title: "Prompt required",
        description: "Please enter a prompt to generate AI content.",
        variant: "destructive"
      });
      return;
    }
    
    if (!serviceStatus.anyConnected) {
      toast({
        title: "AI service not connected",
        description: "Please set up your AI service in settings.",
        variant: "destructive"
      });
      return;
    }
    
    setGeneratingText(true);
    
    try {
      // Simulate AI text generation - in a real app, this would call an API
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      let response = '';
      
      switch (textType) {
        case 'slogan':
          response = generateSampleSlogan(prompt, creativity);
          break;
        case 'headline':
          response = generateSampleHeadline(prompt, creativity);
          break;
        case 'offer':
          response = generateSampleOffer(prompt, creativity);
          break;
        default:
          response = generateSampleSlogan(prompt, creativity);
      }
      
      setAIResponse(response);
      onContentGenerated(response);
      
      toast({
        title: "Content generated",
        description: "AI has generated content based on your prompt."
      });
    } catch (error) {
      console.error('Error generating AI text:', error);
      toast({
        title: "Generation failed",
        description: "Failed to generate AI content. Please try again.",
        variant: "destructive"
      });
    } finally {
      setGeneratingText(false);
    }
  };
  
  const handleImageGeneration = async () => {
    if (!prompt) {
      toast({
        title: "Prompt required",
        description: "Please enter a prompt to generate an AI image.",
        variant: "destructive"
      });
      return;
    }
    
    if (!serviceStatus.anyConnected) {
      toast({
        title: "AI service not connected",
        description: "Please set up your AI service in settings.",
        variant: "destructive"
      });
      return;
    }
    
    setGeneratingImage(true);
    
    try {
      // Simulate AI image generation - in a real app, this would call an API
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Generate a random placeholder image for demo purposes
      const imageUrl = `https://picsum.photos/800/600?random=${Date.now()}`;
      
      onImageGenerated(imageUrl);
      
      toast({
        title: "Image generated",
        description: "AI has generated an image based on your prompt."
      });
    } catch (error) {
      console.error('Error generating AI image:', error);
      toast({
        title: "Image generation failed",
        description: "Failed to generate AI image. Please try again.",
        variant: "destructive"
      });
    } finally {
      setGeneratingImage(false);
    }
  };
  
  const handleUseResponse = () => {
    if (!aiResponse) return;
    onContentGenerated(aiResponse);
    
    toast({
      title: "Content applied",
      description: "AI-generated content has been applied to the banner."
    });
  };
  
  // Mock AI text generation functions
  const generateSampleSlogan = (prompt: string, creativity: number): string => {
    const slogans = [
      "Elevate Your Experience",
      "Innovation Meets Excellence",
      "Beyond Ordinary, Extraordinary",
      "Power In Every Detail",
      "Imagine More, Achieve More",
      "Redefining Possibilities",
      "The Smart Choice",
      "Quality You Can Trust"
    ];
    
    return slogans[Math.floor(Math.random() * slogans.length)];
  };
  
  const generateSampleHeadline = (prompt: string, creativity: number): string => {
    const headlines = [
      "Introducing The Next Generation",
      "Limited Time Offer - Act Now!",
      "The Solution You've Been Waiting For",
      "Transform Your Life Today",
      "Experience The Difference",
      "New & Improved For You"
    ];
    
    return headlines[Math.floor(Math.random() * headlines.length)];
  };
  
  const generateSampleOffer = (prompt: string, creativity: number): string => {
    const offers = [
      "50% Off For First-Time Customers",
      "Buy One Get One Free",
      "Free Shipping On All Orders",
      "Limited Time Special: 30% Off",
      "Join Now & Save 25%",
      "Exclusive Deal: Save $50"
    ];
    
    return offers[Math.floor(Math.random() * offers.length)];
  };

  const handleAnimatedImageUpload = (file: File, imageUrl: string, isAnimated: boolean) => {
    onImageGenerated(imageUrl);
    if (setIsAnimatedGif) {
      setIsAnimatedGif(isAnimated);
    }
    
    toast({
      title: isAnimated ? "Animated GIF uploaded" : "Image uploaded",
      description: `Your ${isAnimated ? 'animated GIF' : 'image'} has been added to the banner.`
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="ai-prompt">Describe what you want in your banner</Label>
        <Textarea
          id="ai-prompt"
          placeholder="E.g., 'A summer sale promotion for beach accessories with vibrant colors'"
          className="mt-1"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
      </div>
      
      <Tabs defaultValue="text" className="w-full">
        <TabsList className="grid grid-cols-3 mb-4">
          <TabsTrigger value="text" className="flex items-center">
            <MessageSquare className="h-4 w-4 mr-2" />
            Generate Text
          </TabsTrigger>
          <TabsTrigger value="image" className="flex items-center">
            <Image className="h-4 w-4 mr-2" />
            Generate Image
          </TabsTrigger>
          <TabsTrigger value="upload" className="flex items-center">
            <Image className="h-4 w-4 mr-2" />
            Upload Image
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="text" className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label>Text Type</Label>
              <Select 
                value={textType} 
                onValueChange={setTextType}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select text type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="slogan">Catchy Slogan</SelectItem>
                  <SelectItem value="headline">Attention-Grabbing Headline</SelectItem>
                  <SelectItem value="offer">Special Offer/Promotion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <div className="flex items-center justify-between">
                <Label>Creativity Level: {creativity}%</Label>
              </div>
              <Slider
                min={0}
                max={100}
                step={10}
                value={[creativity]}
                onValueChange={(values) => setCreativity(values[0])}
                className="mt-2"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">Conservative</span>
                <span className="text-xs text-gray-500">Creative</span>
              </div>
            </div>
            
            <Button
              onClick={handleTextGeneration}
              disabled={!prompt || generatingText || !serviceStatus.anyConnected}
              className="w-full"
            >
              {generatingText ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate AI Text
                </>
              )}
            </Button>
            
            {aiResponse && (
              <div className="rounded-md border p-4 mt-4">
                <Label className="text-xs text-gray-500 mb-1">AI Generated Response:</Label>
                <p className="font-medium text-center text-lg">{aiResponse}</p>
                <div className="flex justify-end mt-3">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={handleUseResponse}
                  >
                    Use This Text
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="image" className="space-y-4">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label>Image Quality: {creativity}%</Label>
              </div>
              <Slider
                min={0}
                max={100}
                step={10}
                value={[creativity]}
                onValueChange={(values) => setCreativity(values[0])}
                className="mt-2"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-500">Simple</span>
                <span className="text-xs text-gray-500">Detailed</span>
              </div>
            </div>
            
            <Button
              onClick={handleImageGeneration}
              disabled={!prompt || generatingImage || !serviceStatus.anyConnected}
              className="w-full"
            >
              {generatingImage ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate AI Image
                </>
              )}
            </Button>
            
            <p className="text-xs text-gray-500">
              Note: Generated images will automatically be added to your banner.
            </p>
          </div>
        </TabsContent>
        
        <TabsContent value="upload">
          <AnimatedImageUploader
            onImageUploaded={handleAnimatedImageUpload} 
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIContentGenerator;
